import { useEffect, useMemo, useState } from 'react';
import { Box, Button, Chip, Dialog, Stack, Typography } from '@mui/material';
import {
  PrintableQuestion,
  pxToRem,
  toStringBloomLevel,
  useCommonServiceClientContext,
} from '@geneo2-web/shared-ui';
import { Question } from '@protos/content_management/content.db_pb';
import { QAttemptResultEnum } from '@protos/learning_management/lms.db_pb';
import {
  IConceptSummary,
  IPerformanceQuestionData,
  IPerformanceSummary,
} from '../../api/learners';
import { Score } from '../../api/common';
import { IKnowledgeConcept } from '../../KnowledgeConcepts/knowledgeConcepts.types';
import {
  getUserSubmittedDisplayAnswer,
  getCorrectDisplayAnswer,
} from '../PalAnswerOverlay';
import {
  CorrectAnswerSection,
  SolutionSection,
  UserAnswerSection,
} from '../AnswerReviewSections';
import { IModuleMasteryData } from '../../api/module';
import { TopbarCard } from '../EndSessionDrawer/components/TopbarCard';
import { ConceptSection } from '../EndSessionDrawer/components/ConceptSection';
import { StrengthWeaknessSection } from '../PerformanceDrawer/components/StrengthWeaknessSection';
import { BLOOM_CONFIG } from '../PerformanceDrawer/performanceDrawerData';
import { DifficultyBreakdownSection } from '../EndSessionDrawer/components/DifficultyBreakdownSection';

// ═══════════════════════════════════════════════════════════════════════
//  Types
// ═══════════════════════════════════════════════════════════════════════

interface ConceptBreakdownItem {
  id: string;
  name: string;
  emoji: string;
  total: number;
  correct: number;
  bloomLevels: string[];
  level: 'strong' | 'weak' | 'not-assessed';
  status: string;
}

export interface ReviewResult {
  result: IPerformanceSummary;
  moduleMastery?: IModuleMasteryData;
  knowledgeConcept?: IKnowledgeConcept;
}

// ═══════════════════════════════════════════════════════════════════════
//  Helpers
// ═══════════════════════════════════════════════════════════════════════

const formatTime = (s: number) => {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${String(sec).padStart(2, '0')}`;
};

function scoreToIsCorrect(score: number | undefined): boolean {
  return score === 1;
}

function getScoreMessage(pct: number): string {
  if (pct >= 80) return "Excellent! You're well prepared for this topic.";
  if (pct >= 50) return "Good start! Let's build on this.";
  return "Nice effort! There's room to grow.";
}

function buildConceptBreakdown(
  concepts: IConceptSummary[],
  questions: IPerformanceQuestionData[],
  moduleMastery: IModuleMasteryData | undefined,
  knowledgeConcept: IKnowledgeConcept | undefined,
  includeMissingConcepts?: boolean
): ConceptBreakdownItem[] {
  const conceptMap = new Map<string, IPerformanceQuestionData[]>();
  for (const q of questions) {
    if (!q.concept_id) continue;
    const list = conceptMap.get(q.concept_id) ?? [];
    list.push(q);
    conceptMap.set(q.concept_id, list);
  }

  const masteryMap = new Map(
    moduleMastery?.concept_states.map((c) => [
      c.concept_id,
      c.concept_status,
    ]) ?? []
  );

  const assessed: ConceptBreakdownItem[] = concepts.map((c) => {
    const isAssessed = c.total_questions > 0;
    const pct = isAssessed ? c.total_correct_questions / c.total_questions : 0;
    const level: ConceptBreakdownItem['level'] = !isAssessed
      ? 'not-assessed'
      : pct >= 0.5
      ? 'strong'
      : 'weak';

    const conceptQuestions = conceptMap.get(c.concept_id) ?? [];
    const bloomLevels = [
      ...new Set(
        conceptQuestions
          .map((q) => q.metadata.bloom_level)
          .filter((b): b is string => !!b)
      ),
    ]
      .map((b) => toStringBloomLevel(b))
      .filter((v) => !!v);
    const status =
      masteryMap.get(c.concept_id) ||
      (c.total_questions > 0 ? 'developing' : 'not_assessed');

    return {
      id: c.concept_id,
      name: c.title || c.name,
      emoji: c.emoji || '📘',
      total: c.total_questions,
      correct: c.total_correct_questions,
      bloomLevels,
      level,
      status: status == 'beginning' ? 'developing' : status,
    };
  });

  const assessedIds = new Set(assessed.map((c) => c.id));
  const missing: ConceptBreakdownItem[] = (
    includeMissingConcepts ? knowledgeConcept?.topological_order ?? [] : []
  )
    .filter((id) => !assessedIds.has(id))
    .reduce<ConceptBreakdownItem[]>((acc, id) => {
      const node = knowledgeConcept?.nodes?.find((n) => n.id === id);
      if (node) {
        const status = masteryMap.get(node.id) || 'not_assessed';
        acc.push({
          id: node.id,
          name: node.title,
          emoji: node.emoji || '📘',
          total: 0,
          correct: 0,
          bloomLevels: [] as string[],
          level: 'not-assessed',
          status: status == 'beginning' ? 'developing' : status,
        });
      }
      return acc;
    }, []);

  return [...assessed, ...missing];
}

function mapToStrengthWeaknessProps(items: ConceptBreakdownItem[]) {
  return items.map((val) => ({
    name: val.name,
    icon: val.emoji,
    accuracy: val.total > 0 ? formatUpiScore(val.correct / val.total) : 0,
    correct: val.correct,
    incorrect: val.total - val.correct,
    bloomLevels: val.bloomLevels as (keyof typeof BLOOM_CONFIG)[],
  }));
}

// ═══════════════════════════════════════════════════════════════════════
//  Static Styles
// ═══════════════════════════════════════════════════════════════════════

const dialogPaperSx = {
  width: '100%',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  boxShadow: '0 24px 64px rgba(0, 0, 0, 0.25)',
  m: 2,
  minWidth: '100vw',
  maxWidth: '100vw',
  minHeight: '100vh',
  maxHeight: '100vh',
  borderRadius: '0px',
} as const;

const backdropSx = {
  background: 'rgba(0, 0, 0, 0.75)',
  backdropFilter: 'blur(10px)',
} as const;

const headerSxWarmup = {
  background: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)',
  padding: '28px 24px 24px',
  textAlign: 'center',
} as const;

const headerSx = {
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  padding: '28px 24px 24px',
  textAlign: 'center',
} as const;

const contentSx = {
  flex: 1,
  overflowY: 'auto',
  bgcolor: '#f8fafc',
  paddingX: { xs: '20px', md: '30px', lg: '250px' },
  paddingY: '24px',
} as const;

const footerSx = {
  padding: '20px 24px',
  paddingBottom: 'calc(20px + env(safe-area-inset-bottom, 0px))',
  bgcolor: 'white',
  borderTop: '1px solid #f1f5f9',
  display: 'flex',
  gap: '20px',
} as const;

const continueBtnSx = {
  flex: 1,
  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
  color: 'white',
  padding: {
    xs: '8px 14px',
    md: '14px 20px',
  },
  borderRadius: '12px',
  fontSize: 14,
  fontWeight: 600,
  textTransform: 'none',
  boxShadow: '0 4px 14px rgba(245,158,11,0.35)',
  '&:hover': {
    background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 20px rgba(245,158,11,0.45)',
  },
} as const;

const endSessionSx = {
  flex: 1,
  background: '#f1f5f9',
  color: '#475569',
  padding: {
    xs: '8px 14px',
    md: '14px 20px',
  },
  borderRadius: '12px',
  fontSize: 14,
  fontWeight: 600,
  textTransform: 'none',
  '&:hover': {
    background: '#e2e8f0',
    transform: 'translateY(-2px)',
  },
} as const;

const cardSx = {
  backgroundColor: '#fff',
  borderRadius: pxToRem(20),
  padding: pxToRem(28),
  // marginBottom: pxToRem(20),
  boxShadow: '0 2px 12px rgba(0,0,0,0.05)',
  alignSelf: 'stretch',
};

// ═══════════════════════════════════════════════════════════════════════
//  Main Component
// ═══════════════════════════════════════════════════════════════════════

export const PerformancePopup: React.FC<{
  open: boolean;
  onClose: () => void;
  onEndSession?: () => void;
  results?: ReviewResult;
  mode?: 'warmup' | 'challenge' | 'end-session';
}> = ({ open, onClose, results, mode, onEndSession }) => {
  if (!open) {
    return null;
  }
  const result = results?.result;
  const totalQuestions = result?.total_questions ?? 0;
  const correctCount = result?.total_correct_questions ?? 0;
  const incorrectCount = result?.total_incorrect_questions ?? 0;
  const skippedCount = result?.total_unattempted_questions ?? 0;
  const timeSpentCount = result?.total_time_taken ?? 0;
  const questions = result?.questions_data ?? [];
  const concepts = result?.concepts ?? [];
  const difficultyLevelBreakdown = result?.difficulty_level_breakdown ?? {};

  const scorePercent =
    totalQuestions > 0 ? (correctCount / totalQuestions) * 100 : 0;
  const scoreMessage = getScoreMessage(scorePercent);

  const conceptBreakdown = useMemo(
    () =>
      buildConceptBreakdown(
        concepts,
        questions,
        results?.moduleMastery,
        results?.knowledgeConcept,
        mode === 'warmup'
      ),
    [concepts, questions, results?.moduleMastery, results?.knowledgeConcept]
  );

  const strongConcepts = useMemo(
    () => conceptBreakdown.filter((c) => c.level === 'strong'),
    [conceptBreakdown]
  );
  const weakConcepts = useMemo(
    () => conceptBreakdown.filter((c) => c.level === 'weak'),
    [conceptBreakdown]
  );

  const conceptsMastered = conceptBreakdown.filter(
    (c) => c.status === 'mastered'
  ).length;
  const conceptsAttempted = conceptBreakdown.filter((c) => c.total > 0).length;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{ sx: dialogPaperSx }}
      slotProps={{ backdrop: { sx: backdropSx } }}
    >
      <Box sx={mode === 'warmup' ? headerSxWarmup : headerSx}>
        <Typography sx={{ fontSize: 48, mb: 1.5 }}>
          {mode === 'warmup' ? '📊' : mode === 'challenge' ? '🏆' : '🎉'}
        </Typography>
        <Typography
          sx={{
            fontSize: 26,
            fontWeight: 800,
            color: 'white',
            mb: 0.75,
            textShadow: '0 2px 4px rgba(0,0,0,0.1)',
          }}
        >
          {mode === 'warmup'
            ? 'Warmup Complete!'
            : mode === 'challenge'
            ? 'Challenge Complete!'
            : 'Session Complete!'}
        </Typography>
        <Typography
          sx={{ fontSize: 14, color: 'rgba(255,255,255,0.9)', fontWeight: 500 }}
        >
          Here's how you did in {totalQuestions} questions
        </Typography>
      </Box>

      <Box sx={contentSx}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '30px',
            width: '100%',
            mb: '30px',
            alignItems: 'center',
          }}
        >
          <Box sx={cardSx}>
            <TopbarCard
              topbar={{
                score:
                  mode === 'end-session'
                    ? formatUpiScore(
                        results?.moduleMastery?.progress?.upi_score_aggregate ||
                          0
                      )
                    : correctCount,
                total: mode === 'end-session' ? 100 : totalQuestions,
                content: mode === 'end-session' ? 'MASTERY SCORE' : undefined,
                message: mode === 'end-session' ? undefined : scoreMessage,
                stats: {
                  correct: correctCount,
                  incorrect: incorrectCount,
                  skipped: skippedCount,
                  time: formatTime(timeSpentCount),
                },
              }}
            />
          </Box>

          {Object.keys(difficultyLevelBreakdown).length > 0 && (
            <Box sx={cardSx}>
              <DifficultyBreakdownSection
                data={Object.keys(difficultyLevelBreakdown).map((val) => {
                  return {
                    level:
                      val === 'LOW'
                        ? 'easy'
                        : val === 'MEDIUM'
                        ? 'medium'
                        : 'hard',
                    correct: difficultyLevelBreakdown[val].correct,
                    total: difficultyLevelBreakdown[val].total,
                    timeTaken: difficultyLevelBreakdown[val].total_time_taken,
                  };
                })}
              />
            </Box>
          )}

          <Box sx={cardSx}>
            <ConceptSection
              data={{
                noOfAttempted: conceptsAttempted,
                noOfMastered: conceptsMastered,
                list: conceptBreakdown.map((val) => ({
                  name: val.name,
                  icon: val.emoji,
                  correct: val.correct,
                  total: val.total,
                  status: val.status,
                })),
              }}
            />
          </Box>

          <Box sx={cardSx}>
            <StrengthWeaknessSection
              strengths={mapToStrengthWeaknessProps(strongConcepts)}
              weaknesses={mapToStrengthWeaknessProps(weakConcepts)}
              cardHeadingSx={{
                fontSize: pxToRem(14),
              }}
            />
          </Box>
        </Box>

        {questions.length > 0 && (
          <QuestionReviews questions={questions} concepts={concepts} />
        )}
      </Box>

      <Box sx={footerSx}>
        {onEndSession && (
          <Button onClick={onEndSession} sx={endSessionSx}>
            Exit Session →
          </Button>
        )}
        <Button onClick={onClose} sx={continueBtnSx}>
          Continue Learning →
        </Button>
      </Box>
    </Dialog>
  );
};

// ═══════════════════════════════════════════════════════════════════════
//  Question Reviews
// ═══════════════════════════════════════════════════════════════════════

const QuestionReviews: React.FC<{
  questions: IPerformanceQuestionData[];
  concepts: IConceptSummary[];
}> = ({ questions, concepts }) => {
  const [loading, setLoading] = useState<'loading' | 'completed' | 'error'>(
    'loading'
  );
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [geneoQuestions, setGeneoQuestions] = useState<Question[]>([]);

  const { ContentCommonAPIServiceV1ClientWithStatusCodeHandler } =
    useCommonServiceClientContext();

  const conceptMap = useMemo(
    () => new Map(concepts.map((c) => [c.concept_id, c])),
    [concepts]
  );

  useEffect(() => {
    setSelectedIdx(0);
    let cancelled = false;

    (async () => {
      try {
        setLoading('loading');
        const ids = questions
          .map((q) => q?.eventReqBody?.geneo_question_id)
          .filter((v): v is string => !!v);

        if (ids.length) {
          const res =
            await ContentCommonAPIServiceV1ClientWithStatusCodeHandler.fetchQuestions(
              {
                questionIds: ids,
              }
            );
          if (!cancelled) setGeneoQuestions(res?.data?.questions ?? []);
        } else {
          if (!cancelled) setGeneoQuestions([]);
        }
        if (!cancelled) setLoading('completed');
      } catch {
        if (!cancelled) setLoading('error');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [questions]);

  if (loading === 'loading') {
    return (
      <Typography
        sx={{
          fontSize: 16,
          fontWeight: 700,
          color: '#1e293b',
          textAlign: 'center',
          my: '10px',
        }}
      >
        Loading Answers...
      </Typography>
    );
  }

  if (loading === 'error') return null;

  const selectedQ = questions[selectedIdx];
  const geneoQues = selectedQ
    ? geneoQuestions.find(
        (g) => g.questionId === selectedQ.eventReqBody?.geneo_question_id
      )
    : undefined;
  const selectedScore = (selectedQ?.eventReqBody?.score as Score)?.value as
    | number
    | undefined;
  const isSelectedCorrect = scoreToIsCorrect(selectedScore);
  const conceptInfo = selectedQ?.concept_id
    ? conceptMap.get(selectedQ.concept_id)
    : undefined;

  return (
    <>
      <Typography sx={{ fontSize: 18, mb: 1.5 }}>
        📝 Review Your Answers
      </Typography>

      <Stack
        direction="row"
        spacing={1}
        sx={{
          mb: 2.5,
          flexWrap: 'wrap',
          gap: 1,
          background: 'white',
          borderRadius: '16px',
          padding: '18px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
          boxSizing: 'border-box',
        }}
      >
        {questions.map((q, idx) => {
          const score = (q?.eventReqBody?.score as Score)?.value as
            | number
            | undefined;
          const isCorrect = scoreToIsCorrect(score);
          const isSelected = idx === selectedIdx;
          return (
            <Box
              key={idx}
              onClick={() => setSelectedIdx(idx)}
              sx={{
                width: 36,
                height: 36,
                borderRadius: '10px',
                border: `1.5px solid ${
                  isSelected
                    ? isCorrect
                      ? '#22c55e'
                      : '#ef4444'
                    : isCorrect
                    ? '#bbf7d0'
                    : '#fecaca'
                }`,
                bgcolor: isSelected
                  ? isCorrect
                    ? '#dcfce7'
                    : '#fee2e2'
                  : isCorrect
                  ? '#f0fdf4'
                  : '#fef2f2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 13,
                fontWeight: isSelected ? 700 : 600,
                color: isCorrect ? '#16a34a' : '#dc2626',
                cursor: 'pointer',
                boxShadow: isSelected
                  ? `0 0 0 2px ${
                      isCorrect
                        ? 'rgba(34,197,94,0.25)'
                        : 'rgba(239,68,68,0.25)'
                    }`
                  : 'none',
                '&:hover': { transform: 'translateY(-1px)' },
                transition: 'all 0.15s ease',
              }}
            >
              Q {idx + 1}
            </Box>
          );
        })}
      </Stack>

      {selectedQ && geneoQues && (
        <Box
          sx={{
            bgcolor: 'white',
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            border: `1.5px solid ${isSelectedCorrect ? '#dcfce7' : '#fee2e2'}`,
          }}
        >
          <Box sx={{ padding: '18px 18px 0' }}>
            <PrintableQuestion
              imageResizeEnabled={true}
              ques={geneoQues}
              number={selectedIdx + 1}
              hideOptions
              hideQuestionNumber
            />
            {conceptInfo && (
              <Chip
                label={`${conceptInfo.emoji || '📘'} ${
                  conceptInfo.title || conceptInfo.name
                }`}
                size="small"
                sx={{
                  mt: 1.25,
                  bgcolor: '#f1f5f9',
                  color: '#475569',
                  fontSize: 11,
                  fontWeight: 500,
                  height: 24,
                }}
              />
            )}
          </Box>
          <Box sx={{ px: '18px', pb: '18px' }}>
            <UserAnswerSection
              answers={
                selectedQ.eventReqBody?.answer
                  ? getUserSubmittedDisplayAnswer(
                      geneoQues,
                      selectedQ.eventReqBody.answer,
                      selectedScore
                    )
                  : []
              }
            />
            {!isSelectedCorrect && (
              <CorrectAnswerSection
                correctAnswers={
                  selectedQ.eventReqBody?.correct_answer
                    ? getCorrectDisplayAnswer(
                        geneoQues,
                        selectedQ.eventReqBody.correct_answer
                      )
                    : []
                }
                userSubmittedAnswers={
                  selectedQ.eventReqBody?.answer
                    ? getUserSubmittedDisplayAnswer(
                        geneoQues,
                        selectedQ.eventReqBody.answer,
                        selectedScore
                      )
                    : []
                }
              />
            )}
            <SolutionSection solution={selectedQ.metadata.solution || []} />
          </Box>
        </Box>
      )}
    </>
  );
};

export const formatUpiScore = (aggregate: number) => {
  return Math.round(aggregate * 100);
};
